import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical, faMicrophone, faPause, faPlay, faStop, faTrash, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useScreenRecorder, recordingToFile, END_REASONS, endReasonText } from '../hooks/useScreenRecorder';
import { notify, playPing } from '../utils/attention';
import { ScreenRecordingContext } from './ScreenRecordingContext';
import '../styles/screenRecording.css';

/**
 * The app-wide screen recorder and its floating bar. Mounted once in the
 * authenticated layout; see ScreenRecordingContext.js for why it lives here.
 */

const formatTime = (ms) => {
    const s = Math.floor(ms / 1000);
    return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

// Quieter than the message chime: the recording ending is worth noticing, not an alarm.
const ENDED_CHIME_VOLUME = 0.045;

const POSITION_KEY = 'screenRecording.barPosition';
const EDGE = 8; // px kept between the bar and the window edge

const readPosition = () => {
    try { return JSON.parse(localStorage.getItem(POSITION_KEY)) || null; } catch { return null; }
};
const savePosition = (pos) => {
    try {
        if (pos) localStorage.setItem(POSITION_KEY, JSON.stringify(pos));
        else localStorage.removeItem(POSITION_KEY);
    } catch { /* storage unavailable - the bar just won't remember its spot */ }
};

/**
 * Drag the bar anywhere on screen by any part that is not a button. The spot
 * is remembered, and kept inside the window when it is resized. null means
 * the default: centred under the topbar. Double-click returns it there.
 */
const useDraggableBar = () => {
    const barRef = useRef(null);
    const dragRef = useRef(null);
    const [pos, setPos] = useState(readPosition);

    const clamp = useCallback((x, y) => {
        const el = barRef.current;
        const w = el?.offsetWidth || 0;
        const h = el?.offsetHeight || 0;
        return {
            x: Math.round(Math.min(Math.max(EDGE, x), window.innerWidth - w - EDGE)),
            y: Math.round(Math.min(Math.max(EDGE, y), window.innerHeight - h - EDGE))
        };
    }, []);

    // Re-fit whenever the bar appears: it is only now measurable, and a spot
    // saved on a bigger window can be partly off this one.
    const setBarRef = useCallback((el) => {
        barRef.current = el;
        if (el) setPos((p) => (p ? clamp(p.x, p.y) : p));
    }, [clamp]);

    // ...and keep it on screen as the window is resized.
    useEffect(() => {
        const fit = () => setPos((p) => (p ? clamp(p.x, p.y) : p));
        fit();
        window.addEventListener('resize', fit);
        return () => window.removeEventListener('resize', fit);
    }, [clamp]);

    const onPointerDown = (e) => {
        if (e.button !== 0 || e.target.closest('button')) return;
        const rect = barRef.current.getBoundingClientRect();
        dragRef.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top, last: null };
        barRef.current.setPointerCapture?.(e.pointerId);
        e.preventDefault();
    };

    const onPointerMove = (e) => {
        const drag = dragRef.current;
        if (!drag) return;
        drag.last = clamp(e.clientX - drag.dx, e.clientY - drag.dy);
        barRef.current.classList.add('dragging');
        setPos(drag.last);
    };

    const onPointerUp = (e) => {
        const drag = dragRef.current;
        if (!drag) return;
        dragRef.current = null;
        barRef.current?.classList.remove('dragging');
        barRef.current?.releasePointerCapture?.(e.pointerId);
        if (drag.last) savePosition(drag.last);
    };

    const onDoubleClick = (e) => {
        if (e.target.closest('button')) return;
        setPos(null);
        savePosition(null);
    };

    return {
        ref: setBarRef,
        style: pos ? { left: pos.x, top: pos.y, translate: 'none' } : undefined,
        onPointerDown,
        onPointerMove,
        onPointerUp,
        onPointerCancel: onPointerUp,
        onDoubleClick,
        title: 'Drag to move · double-click to reset'
    };
};

export const ScreenRecordingProvider = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dragProps = useDraggableBar();

    // Stable draft key, title and return URL of the recording's destination.
    const [origin, setOrigin] = useState(null);
    const originRef = useRef(null);
    // Set by Discard so the take that stopping produces is thrown away.
    const cancelledRef = useRef(false);

    // Destination key -> File, waiting for its composer or form to stage it.
    const [drafts, setDrafts] = useState({});
    // The chat the latest draft was saved to, for the "saved to…" notice.
    const [notice, setNotice] = useState(null);

    const recorder = useScreenRecorder({
        onRecordingComplete: (blob, { reason = END_REASONS.USER } = {}) => {
            const target = originRef.current;
            if (cancelledRef.current || !target) return;

            // A recorder that failed before capturing anything leaves no file.
            const empty = !blob || blob.size === 0;
            if (!empty) setDrafts((prev) => ({ ...prev, [target.draftKey]: recordingToFile(blob) }));

            // Null when the user pressed Stop: they know why it ended.
            const why = endReasonText(reason, { empty });
            setNotice({ ...target, why, empty });
            if (!why) return;

            // It ended on its own, and whoever was recording is usually looking
            // at the window they were recording, not at this tab: a quiet chime,
            // and an OS notification unless this window is the one in front.
            playPing(ENDED_CHIME_VOLUME);
            notify({
                title: 'Screen recording stopped',
                body: empty ? why : `${why} Saved as a draft in ${target.title || 'the chat'}.`,
                tag: 'screen-recording-ended',
                force: !document.hasFocus(),
                onClick: () => { if (!empty) navigate(target.href); }
            });
        }
    });
    const { status, discardRecording } = recorder;

    // The hook parks in 'preview' after stopping; this flow has no preview
    // step, the draft is the preview, so reset straight away for the next take.
    useEffect(() => {
        if (status !== 'preview') return;
        discardRecording();
        cancelledRef.current = false;
        originRef.current = null;
        setOrigin(null);
    }, [status, discardRecording]);

    // requesting -> idle means the screen picker was cancelled (or refused):
    // nothing was recorded, so the chat it was for is forgotten.
    const prevStatusRef = useRef(status);
    useEffect(() => {
        if (prevStatusRef.current === 'requesting' && status === 'idle') {
            originRef.current = null;
            setOrigin(null);
        }
        prevStatusRef.current = status;
    }, [status]);

    const active = status === 'requesting' || status === 'recording' || status === 'paused';

    const start = useCallback((target) => {
        if (active || originRef.current) {
            Swal.fire(
                'Already recording',
                `Stop the recording for ${originRef.current?.title || 'the other destination'} before starting another.`,
                'info'
            );
            return;
        }
        cancelledRef.current = false;
        const destination = {
            ...target,
            draftKey: target.draftKey || target.conversationId,
            href: target.href || (location.pathname.startsWith('/portal/')
                ? location.pathname : `/chats/${target.conversationId}`)
        };
        originRef.current = destination;
        setOrigin(destination);
        recorder.startRecording();
    }, [active, recorder, location.pathname]);

    const discard = () => {
        cancelledRef.current = true;
        recorder.stopRecording();
    };

    // Called by the composer once it has staged the draft.
    const takeDraft = useCallback((conversationId) => {
        setDrafts((prev) => {
            if (!prev[conversationId]) return prev;
            const next = { ...prev };
            delete next[conversationId];
            return next;
        });
    }, []);

    // Only worth showing while the draft is still waiting: once its chat has
    // picked it up, the user is already looking at it.
    // A notice that explains why the recording ended stays until it is dismissed,
    // even after the chat has picked the draft up (which happens at once when
    // that chat is the one on screen) and even with no draft at all.
    const showNotice = notice && (notice.why || drafts[notice.draftKey]) && !active;

    return (
        <ScreenRecordingContext.Provider value={{ start, drafts, takeDraft, isRecording: active, origin }}>
            {children}

            {(status === 'recording' || status === 'paused') && (
                <div className="srec-bar" role="status" {...dragProps}>
                    <FontAwesomeIcon icon={faGripVertical} className="srec-grip" />
                    <span className={`srec-dot ${status === 'recording' ? 'live' : ''}`} />
                    <div className="srec-text">
                        <strong>{formatTime(recorder.recordingTimeMs)}</strong>
                        <span title={`Recording for ${origin?.title || 'chat'}`}>{origin?.title || 'chat'}</span>
                    </div>
                    <div className="srec-mic" role="img" aria-label={recorder.micAvailable ? (status === 'paused' ? 'Voice recording paused' : 'Live microphone waveform') : 'Microphone unavailable: recording without voice'} title={recorder.micAvailable ? 'Microphone waveform' : 'Microphone unavailable: recording without voice'}>
                        <FontAwesomeIcon icon={faMicrophone} />
                        <span className="srec-waveform" aria-hidden="true">
                            {recorder.micWaveform.map((level, index) => (
                                <span key={index} style={{ transform: `scaleY(${status === 'recording' && recorder.micAvailable ? Math.max(0.08, level) : 0.08})` }} />
                            ))}
                        </span>
                        {!recorder.micAvailable && <span>No mic</span>}
                    </div>
                    {status === 'recording' ? (
                        <button type="button" className="srec-btn" onClick={recorder.pauseRecording} title="Pause">
                            <FontAwesomeIcon icon={faPause} />
                        </button>
                    ) : (
                        <button type="button" className="srec-btn" onClick={recorder.resumeRecording} title="Resume">
                            <FontAwesomeIcon icon={faPlay} />
                        </button>
                    )}
                    <button type="button" className="srec-btn" onClick={discard} title="Discard recording">
                        <FontAwesomeIcon icon={faTrash} />
                    </button>
                    <button type="button" className="srec-btn stop" onClick={recorder.stopRecording}
                        title={`Stop and add to ${origin?.title || 'the chat'} as a draft`}>
                        <FontAwesomeIcon icon={faStop} />
                    </button>
                </div>
            )}

            {showNotice && (
                <div className={`srec-bar srec-notice ${notice.why ? 'srec-why' : ''}`} role="status" {...dragProps}>
                    <FontAwesomeIcon icon={faGripVertical} className="srec-grip" />
                    <span className="srec-notice-text">
                        {notice.why ? <>{notice.why}{' '}</> : null}
                        {notice.empty ? null : <>Saved as a draft in <strong>{notice.title}</strong></>}
                    </span>
                    {notice.empty ? null : (
                        <button
                            type="button"
                            className="srec-btn stop"
                            onClick={() => navigate(notice.href)}
                        >
                            {notice.conversationId ? 'Open chat' : 'Open form'}
                        </button>
                    )}
                    <button type="button" className="srec-btn" onClick={() => setNotice(null)} title="Dismiss">
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>
            )}
        </ScreenRecordingContext.Provider>
    );
};
